import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { paths } from 'config'
import { useAuthParams } from 'shared/hooks/use-auth-params'
import { useIsBeforeTime } from 'shared/hooks/use-is-before-time'
import { useSessionExpiresAt } from '../SessionExpiresAtContext'
import { useIsExtendingSession } from '../utils/sessionExtension'
import {
  getSessionGracePeriodEnd,
  holdSessionExpiry,
} from '../utils/sessionExpiryHold'
import { useIsSessionExpiryHeldByAnotherTab } from './use-is-session-expiry-held-by-another-tab'

const REAUTHENTICATION_PATHS: string[] = [paths.signIn, paths.signInWithLink]

const useIsReauthenticationScreen = () => {
  const { pathname } = useLocation()
  const { reauthenticate } = useAuthParams()

  return reauthenticate && REAUTHENTICATION_PATHS.includes(pathname)
}

// An expired session isn't ended during a reauthentication, which would make it
// fail. The reauthentication screen shows no data, so it waits for the user to
// leave it; the session extension dialog and the other tabs wait only for the
// grace period. See README.md in shared/session.
// Reads the route, so it has to be used inside the router.
export const useIsSessionExpiryPaused = () => {
  const isReauthenticationScreen = useIsReauthenticationScreen()
  const isExtendingSession = useIsExtendingSession()
  const sessionExpiresAt = useSessionExpiresAt()
  const gracePeriodEnd = getSessionGracePeriodEnd(sessionExpiresAt)
  const isHeldByAnotherTab = useIsSessionExpiryHeldByAnotherTab(gracePeriodEnd)
  const isWithinGracePeriod = useIsBeforeTime(gracePeriodEnd)

  useEffect(() => {
    if (!isReauthenticationScreen) return
    return holdSessionExpiry()
  }, [isReauthenticationScreen])

  // Effects aren't cleaned up when the page is left, and the other tabs would
  // wait for a stale hold until it runs out. The reloaded tab would too.
  useEffect(() => {
    if (!isExtendingSession) return

    const stopHold = holdSessionExpiry()
    window.addEventListener('pagehide', stopHold)

    return () => {
      window.removeEventListener('pagehide', stopHold)
      stopHold()
    }
  }, [isExtendingSession])

  return (
    isReauthenticationScreen ||
    (isExtendingSession && isWithinGracePeriod) ||
    isHeldByAnotherTab
  )
}
